/**
 * Turns query string like hello=1&another=2 into object {hello: 1, another: 2}
 * @param {*} queryString
 */

function parseQuery(queryString) {
  var query = {};
  var pairs = (
    queryString[0] === "?" ? queryString.substring(1) : queryString
  ).split("&");
  for (var i = 0; i < pairs.length; i++) {
    var pair = pairs[i].split("=");
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || "");
  }
  return query;
}

function modifyColor(col, amt) {
  var usePound = false;

  if (col[0] == "#") {
    col = col.slice(1);
    usePound = true;
  }

  var num = parseInt(col, 16);

  var r = (num >> 16) + amt;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  var b = ((num >> 8) & 0x00ff) + amt;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  var g = (num & 0x0000ff) + amt;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (
    (usePound ? "#" : "") +
    String("000000" + (g | (b << 8) | (r << 16)).toString(16)).slice(-6)
  );
}

function addAlpha(color, opacity) {
  const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + _opacity.toString(16).toUpperCase();
}

function parsePrice(price, thousandSeparator, decimalSeparator) {
  if (!price) {
    return "";
  }
  const regEx = new RegExp(
    `([0-9${thousandSeparator}]+)(\\${decimalSeparator}\\d+)?`
  );
  const [_ignored, int, decimals] = price.match(regEx);
  return parseFloat(
    `${int.replace(/[^0-9]/g, "")}.${(decimals || "0").replace(/[^0-9]/g, "")}`
  );
}

function formatPrice(price) {
  if (!price) {
    return "";
  }
  let cleanedPrice = price.toString().replace("kr", "").trim();

  cleanedPrice = cleanedPrice.split(".")[0];

  cleanedPrice = cleanedPrice.replace(",", " ");

  let formattedPrice = `${cleanedPrice},-`;

  return formattedPrice;
}

// Formats a number with space as thousand separator, e.g. 12500 -> "12 500"
function formatThousands(price) {
  return (price ?? 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/**
 * Calculates the travel price based on the chosen travel.
 * Travellers in an "Enkeltrom" pay the single room supplement on top of the
 * regular price per person. A request (see isTravelRequest) has no price yet:
 * the office confirms it in the offer, so never show or send this total for a
 * request.
 * @param {*} chosenTravel
 */
function calculateTravelPrice(chosenTravel) {
  const { travelProduct, hotel, travellers } = chosenTravel;
  const pricePerPerson = Number(travelProduct.priceRaw) || 0;
  const singleRoomSupplement = hotel.hasHotel
    ? Number(travelProduct.singleRoomSupplement) || 0
    : 0;

  const numberOfTravellers = travellers.otherTravellers.reduce(
    (total, room) => total + room.people.length,
    0
  );
  const singleRoomTravellers = hotel.hasHotel
    ? hotel.rooms
        .filter((room) => room.typeOfRoom === "Enkeltrom")
        .reduce((total, room) => total + room.adults + room.children, 0)
    : 0;

  return {
    pricePerPerson,
    singleRoomSupplement,
    numberOfTravellers,
    singleRoomTravellers,
    totalPrice:
      pricePerPerson * numberOfTravellers +
      singleRoomSupplement * singleRoomTravellers,
  };
}

/* Hotel room types. Travelling alone gives a single room. Someone travelling
 * alone can ask for a double room, but then the booking is not automatic: it
 * is sent as a request (forespørsel), and the office confirms the price with
 * the hotel. Children are not part of the automatic booking, so rooms and
 * cabins always have children: 0. */
const ROOM_TYPE_SINGLE = "Enkeltrom";
const ROOM_TYPE_DOUBLE = "Dobbeltrom";
const ROOM_TYPE_DOUBLE_ALONE = "Dobbeltrom (reiser alene)";
const ROOM_TYPES = [ROOM_TYPE_SINGLE, ROOM_TYPE_DOUBLE, ROOM_TYPE_DOUBLE_ALONE];

const CABIN_TYPES = [
  "Enkellugar - Innvendig",
  "Enkellugar - Utvendig",
  "Dobbellugar - Innvendig",
  "Dobbellugar - Utvendig",
];
const MAX_ADULTS_IN_CABIN = 6;

// A double room has two adults, every other room type one.
function getAdultsForRoomType(typeOfRoom) {
  return typeOfRoom === ROOM_TYPE_DOUBLE ? 2 : 1;
}

function createHotelRoom(typeOfRoom = ROOM_TYPE_SINGLE) {
  return {
    adults: getAdultsForRoomType(typeOfRoom),
    children: 0,
    ageOfChildren: [],
    typeOfRoom,
  };
}

// The first cabin follows the travel: one adult with a hotel (like the first
// room), two on a ferry-only travel.
function createCabin(hasHotel) {
  return {
    adults: hasHotel ? 1 : 2,
    children: 0,
    ageOfChildren: [],
    typeOfCabin: CABIN_TYPES[0],
  };
}

// Travellers on a travel without hotel or ferry are kept in rooms[0].
function createTravellersOnlyRoom(adults) {
  return {
    adults: Math.max(1, Number(adults) || 1),
    children: 0,
    ageOfChildren: [],
  };
}

function hasRequestRoom(rooms) {
  return (rooms || []).some(
    (room) => room.typeOfRoom === ROOM_TYPE_DOUBLE_ALONE
  );
}

/**
 * True when the booking is a request instead of an automatic booking: a
 * hotel travel where someone travelling alone asked for a double room.
 * @param {*} chosenTravel
 */
function isTravelRequest(chosenTravel) {
  return (
    Boolean(chosenTravel?.hotel?.hasHotel) &&
    hasRequestRoom(chosenTravel.hotel.rooms)
  );
}

// The rooms step 1 starts with.
function getInitialRooms(chosenTravel) {
  const { hasHotel, rooms } = chosenTravel.hotel;
  if (rooms?.length > 0) {
    return hasHotel
      ? rooms.map((room) => ({ ...room }))
      : [createTravellersOnlyRoom(rooms[0].adults)];
  }
  return hasHotel ? [createHotelRoom()] : [createTravellersOnlyRoom(2)];
}

// The ferry cabins step 1 starts with.
function getInitialCabins(chosenTravel) {
  const { hasFerry, cabins } = chosenTravel.ferry;
  if (!hasFerry) {
    return [];
  }
  return cabins?.length > 0
    ? cabins.map((cabin) => ({ ...cabin }))
    : [createCabin(chosenTravel.hotel.hasHotel)];
}

// The contact page, with the contact form, of the current site.
function getContactPageUri() {
  return isBuggeBusSite() ? "/kontakt-oss/" : "/kontakt/";
}

// Only the fields the RoomInput and CabinInput GraphQL types accept.
function toRoomInput(room) {
  return {
    adults: room.adults,
    children: room.children || 0,
    ageOfChildren: room.ageOfChildren || [],
    typeOfRoom: room.typeOfRoom,
  };
}

function toCabinInput(cabin) {
  return {
    adults: cabin.adults,
    children: cabin.children || 0,
    ageOfChildren: cabin.ageOfChildren || [],
    typeOfCabin: cabin.typeOfCabin,
  };
}

/**
 * Brings a chosen travel saved by an older version of the checkout in line
 * with the current rules: no children, a double room has two adults (one
 * adult in an old "Dobbeltrom" becomes "Enkeltrom"), cabins have 1–6 adults.
 * Returns a new object and never changes the input. resetStep is true when
 * something the customer chose was changed, or rooms or cabins are missing,
 * so the checkout should start again from step 1.
 * @param {*} chosenTravel
 */
function normalizeChosenTravel(chosenTravel) {
  if (!chosenTravel?.travelProduct?.id) {
    return { chosenTravel, resetStep: false };
  }
  const { travellers } = chosenTravel;
  // Travels saved before the ferry option existed have no ferry key
  const hotel = chosenTravel.hotel || { hasHotel: false, rooms: [] };
  const ferry = chosenTravel.ferry || { hasFerry: false, cabins: [] };
  let changed = !chosenTravel.hotel || !chosenTravel.ferry;

  const rooms = Array.isArray(hotel.rooms)
    ? hotel.rooms.map((room) => {
        if (!hotel.hasHotel) {
          const cleaned = createTravellersOnlyRoom(room.adults);
          if (cleaned.adults !== room.adults || room.children > 0) {
            changed = true;
          }
          return cleaned;
        }
        let type = room.typeOfRoom;
        if (!ROOM_TYPES.includes(type)) {
          type = room.adults >= 2 ? ROOM_TYPE_DOUBLE : ROOM_TYPE_SINGLE;
        } else if (type === ROOM_TYPE_DOUBLE && room.adults < 2) {
          type = ROOM_TYPE_SINGLE;
        } else if (type === ROOM_TYPE_SINGLE && room.adults >= 2) {
          // Older checkouts started every room as Enkeltrom with 2 adults
          type = ROOM_TYPE_DOUBLE;
        }
        const cleaned = createHotelRoom(type);
        if (
          cleaned.typeOfRoom !== room.typeOfRoom ||
          cleaned.adults !== room.adults ||
          room.children > 0
        ) {
          changed = true;
        }
        return cleaned;
      })
    : hotel.rooms;

  const cabins = Array.isArray(ferry.cabins)
    ? ferry.cabins.map((cabin) => {
        const adults = Math.min(
          MAX_ADULTS_IN_CABIN,
          Math.max(1, Number(cabin.adults) || 1)
        );
        const typeOfCabin = CABIN_TYPES.includes(cabin.typeOfCabin)
          ? cabin.typeOfCabin
          : CABIN_TYPES[0];
        if (
          adults !== cabin.adults ||
          typeOfCabin !== cabin.typeOfCabin ||
          cabin.children > 0
        ) {
          changed = true;
        }
        return { adults, children: 0, ageOfChildren: [], typeOfCabin };
      })
    : ferry.cabins;

  const otherTravellers = (travellers?.otherTravellers || []).map((unit) => {
    const people = (unit.people || []).filter((person) => !person.child);
    if (people.length !== (unit.people || []).length) {
      changed = true;
    }
    return { ...unit, people };
  });

  const missingRooms =
    (hotel.hasHotel && !(rooms?.length > 0)) ||
    (ferry.hasFerry && !(cabins?.length > 0));

  return {
    chosenTravel: {
      ...chosenTravel,
      hotel: { ...hotel, rooms },
      ferry: { ...ferry, cabins },
      travellers: {
        ...travellers,
        otherTravellers,
        comments:
          typeof travellers?.comments === "string" ? travellers.comments : "",
      },
    },
    resetStep: changed || Boolean(missingRooms),
  };
}

function checkGTM() {
  const scripts = document.head.getElementsByTagName("script");
  for (let i = 0; i < scripts.length; i++) {
    if (scripts[i].src.includes("www.googletagmanager.com")) {
      return true;
    }
  }
  return false;
}

const updateDataLayer = (datalayerFunction) => {
  const interval = setInterval(async () => {
    if (checkGTM()) {
      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push({ ecommerce: null });
      await datalayerFunction();
      clearInterval(interval);
    }
  }, 200);
};

const norwegianMonths = [
  "januar",
  "februar",
  "mars",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "desember",
];

function formatDate(dateString) {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = norwegianMonths[date.getMonth()].slice(0, 3);
  const year = date.getFullYear();

  return {
    day: day.toString().padStart(2, "0"),
    month,
    year: year.toString(),
  };
}

/**
 * Capitalized Norwegian month name, e.g. "Oktober". The short version keeps
 * names of up to four letters whole: "Okt", "Mars", "Juni".
 * @param {Date} date
 * @param {boolean} short
 */
function formatMonthName(date, short = false) {
  const month = norwegianMonths[date.getMonth()];
  const name = short && month.length > 4 ? month.slice(0, 3) : month;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function formatDateWithWeekDay(dateString) {
  const date = new Date(dateString);
  const options = { weekday: "long" };
  let weekdayName = date.toLocaleString("nb-NO", options);
  weekdayName = weekdayName.charAt(0).toUpperCase() + weekdayName.slice(1);
  return `${weekdayName}, ${dateString}`;
}

function removeHtmlTags(text) {
  const regex = /(<([^>]+)>)/gi;
  return text.replace(regex, " ");
}

function extractFirstNumber(str) {
  const match = str?.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

const isBuggeBusSite = () => {
  return process.env.GATSBY_WORDPRESS_URL === "https://admin.buggebuss.no";
};

module.exports = {
  parseQuery,
  addAlpha,
  modifyColor,
  parsePrice,
  formatPrice,
  formatThousands,
  calculateTravelPrice,
  updateDataLayer,
  formatDate,
  formatMonthName,
  removeHtmlTags,
  extractFirstNumber,
  formatDateWithWeekDay,
  isBuggeBusSite,
  ROOM_TYPE_SINGLE,
  ROOM_TYPE_DOUBLE,
  ROOM_TYPE_DOUBLE_ALONE,
  CABIN_TYPES,
  MAX_ADULTS_IN_CABIN,
  createHotelRoom,
  createCabin,
  hasRequestRoom,
  isTravelRequest,
  getInitialRooms,
  getInitialCabins,
  getContactPageUri,
  toRoomInput,
  toCabinInput,
  normalizeChosenTravel,
};
